import { createTheme } from "@mui/material/styles";

// Modern customer-facing palette — same brand colours as the admin app but
// with softer rounding and a brighter background to feel approachable.
const PALETTE = {
  brand: "#0d2e75",
  brandDark: "#08205a",
  brandLight: "#1f49a8",
  accent: "#ee5b29",
  accentDark: "#c0451d",
  accentLight: "#ff7a30",
  sun: "#f5a623",
  sky: "#5fb3e3",
  pageBg: "#f5f7fb",
  paper: "#ffffff",
  text: "#1c2733",
  textSub: "#5a6573",
  border: "#e3e8ef",
  positive: "#107e3e",
  positiveSoft: "#e8f5ec",
  warningSoft: "#fff4e3",
  negative: "#bb0000",
  negativeSoft: "#fdecec",
};

export const BRAND_GRADIENT = `linear-gradient(135deg, ${PALETTE.brandDark} 0%, ${PALETTE.brand} 55%, ${PALETTE.accent} 130%)`;
export const SUN_GRADIENT = `linear-gradient(135deg, #e63946 0%, ${PALETTE.accentLight} 60%, ${PALETTE.sun} 100%)`;
export const HERO_GRADIENT = `linear-gradient(120deg, ${PALETTE.brand} 0%, ${PALETTE.brandLight} 55%, ${PALETTE.accent} 130%)`;

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: PALETTE.brand,
      dark: PALETTE.brandDark,
      light: PALETTE.brandLight,
      contrastText: "#fff",
    },
    secondary: {
      main: PALETTE.accent,
      dark: PALETTE.accentDark,
      light: PALETTE.accentLight,
      contrastText: "#fff",
    },
    background: { default: PALETTE.pageBg, paper: PALETTE.paper },
    text: { primary: PALETTE.text, secondary: PALETTE.textSub },
    success: { main: PALETTE.positive },
    warning: { main: PALETTE.accent },
    error: { main: PALETTE.negative },
    info: { main: PALETTE.sky },
    divider: PALETTE.border,
  },
  shape: { borderRadius: 10 },
  spacing: 4,
  typography: {
    fontFamily: '"Inter", "Helvetica Neue", Arial, sans-serif',
    fontSize: 14,
    htmlFontSize: 16,
    h1: { fontSize: "1.75rem", fontWeight: 700, color: PALETTE.brand, letterSpacing: -0.2 },
    h2: { fontSize: "1.375rem", fontWeight: 700 },
    h3: { fontSize: "1.125rem", fontWeight: 600 },
    h4: { fontSize: "1rem", fontWeight: 600 },
    h5: { fontSize: "0.9375rem", fontWeight: 600 },
    h6: { fontSize: "0.875rem", fontWeight: 600 },
    body1: { fontSize: "0.875rem" },
    body2: { fontSize: "0.8125rem" },
    button: { fontSize: "0.875rem", textTransform: "none", fontWeight: 600 },
    caption: { fontSize: "0.75rem", color: PALETTE.textSub },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: PALETTE.pageBg,
          color: PALETTE.text,
        },
        "*::-webkit-scrollbar": { width: 8, height: 8 },
        "*::-webkit-scrollbar-thumb": { backgroundColor: "#c8cfd9", borderRadius: 4 },
        "*::-webkit-scrollbar-track": { backgroundColor: "transparent" },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true, size: "medium" },
      styleOverrides: {
        root: {
          borderRadius: 10,
          minHeight: 36,
          paddingTop: 6,
          paddingBottom: 6,
          paddingLeft: 14,
          paddingRight: 14,
        },
        containedSecondary: {
          background: SUN_GRADIENT,
          "&:hover": { background: SUN_GRADIENT, filter: "brightness(0.95)" },
        },
      },
    },
    MuiIconButton: {
      defaultProps: { size: "small" },
      styleOverrides: { root: { borderRadius: 8 } },
    },
    MuiTextField: {
      defaultProps: { size: "small", variant: "outlined" },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: "#fff",
          fontSize: "0.875rem",
        },
        input: { padding: "10px 12px" },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: "none", borderRadius: 12 },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          border: `1px solid ${PALETTE.border}`,
          borderRadius: 12,
          backgroundColor: PALETTE.paper,
          boxShadow: "0 1px 2px rgba(13,46,117,0.04), 0 4px 16px rgba(13,46,117,0.04)",
        },
      },
    },
    MuiCardContent: {
      styleOverrides: { root: { padding: 16, "&:last-child": { paddingBottom: 16 } } },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: "#fff",
          color: PALETTE.text,
          borderBottom: `1px solid ${PALETTE.border}`,
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: { minHeight: "64px !important", paddingLeft: 16, paddingRight: 16 },
        dense: { minHeight: "56px !important" },
      },
    },
    MuiChip: {
      defaultProps: { size: "small" },
      styleOverrides: {
        root: { borderRadius: 999, height: 22, fontSize: "0.6875rem", fontWeight: 600 },
      },
    },
    MuiAvatar: {
      styleOverrides: { root: { width: 36, height: 36, fontSize: "0.875rem" } },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: PALETTE.brandDark,
          fontSize: "0.75rem",
          borderRadius: 8,
        },
      },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: PALETTE.border } },
    },
  },
});

export default theme;
